/**
 * DocumentsPage Component
 *
 * This is the main documents management page for workspaces.
 * It provides:
 * - Document upload (files and URLs)
 * - Collection management (regular and smart collections)
 * - Document viewing and organization
 * - AI-powered collection suggestions
 * - Document grid and folder view modesP
 * - PDF viewer integration
 * - SignalR real-time updates
 *
 * The component uses the useDocuments hook for state management and integrates
 * with SignalR for real-time workspace updates.
 */

// ============= Imports =============

// React hooks
import { useState, useMemo, useEffect } from 'react';

// Routing
import { useParams, useSearchParams } from 'react-router-dom';

// Styled components
import {
  PageContainer,
  ContentSectionContainer,
  ContentSection,
  FormGroup,
  FormLabel,
  FormInput,
  FormTextarea,
  FormSelect,
  FormActions,
  CheckboxLabel,
  CheckboxInput,
  ToolbarContainer,
  UploadStatusTile,
  UploadStatusIcon,
  UploadStatusContent,
  UploadStatusTitle,
  UploadStatusMessage,
  UploadProgressBarTrack,
  UploadProgressBarFill,
  UploadStatusActions,
  ModalSectionLabel,
  ModalHintText,
  ModalDocumentName,
  PendingFileList,
  PendingFileRow,
  PendingFileInfo,
  PendingFileRemove,
  PendingFileError,
  SuggestionList,
  SuggestionRow,
  SuggestionInfo,
  SuggestionTitleRow,
  SuggestionName,
  SuggestionBadge,
  SuggestionCurrentTag,
  SuggestionReason,
  SuggestionEmpty,
  DeleteList,
  DeleteListRow,
} from './DocumentsPage.styles';

// Component library
import { LoadingSpinner } from '@tw/tw-component-library';
import { Button, Modal, Input } from '@/ui';

// Layout components
import { WorkspaceHeaderWithErrorBoundary } from '@/components/layout/WorkspaceHeaderLayout';
import { WorkspaceErrorDisplay } from '@/components/common/ErrorBoundary/WorkspaceErrorDisplay';

// Document components
import { Toolbar as DocumentsToolbar } from '@/components/common/Documents/Toolbar/Toolbar';
import { CollectionsSection } from '@/components/common/Documents/CollectionsSection/CollectionsSection';
import { EmptyState } from '@/components/common/Documents/EmptyState/EmptyState';
import { DocumentGrid } from '@/components/common/Documents/DocumentGrid/DocumentGrid';
import { PdfViewer } from '@/components/common/Documents/PdfViewer/PdfViewer';
import { FolderView } from '@/components/common/Documents/FolderView/FolderView';

// Filter components
import { FilterPills } from '@/components/common/FilterPills';

// Styles
import { PlaceholderText } from '../askHub/AskHub.styles';

// APIs
import { postCollection } from '@/apis/data/dataRoom/post/postCollection';

// Types
import { Collection, DataRoomFile } from '@/types/documents/documents.types';

// Contexts
import { UseAuth } from '@/contexts/AuthContext';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';
import { ApiError } from '@/utils/errors/apiErrorWrapper';

// Hooks
import { useDocuments } from '@/hooks/documents/useDocuments';
import { useCompactTheme } from '@/hooks/useCompactTheme';
import { useSignalRHub } from '@/hooks/useSignalRHub';
import { useWorkspaceRelayHubHandlers } from '@/hooks/signalR/useWorkspaceRelayHubHandlers';
import { WorkspaceRelayHubEvents } from '@/handlers/signalR/workspaceRelayHub';

// Icons
import {
  FiAlertTriangle,
  FiFile,
  FiLink,
  FiRefreshCw,
  FiTrash2,
  FiUploadCloud,
  FiX,
} from 'react-icons/fi';

// Utilities
import {
  extractCollectionId,
  extractDocumentCollectionId,
  findCollectionByName,
  truncateText,
  formatConfidence,
  getCollectionRefreshDelay,
  getCollectionDisplayName,
  getCollectionIcon,
  CollectionSuggestion,
} from './DocumentsPage.utils';
import { UploadMode } from './DocumentsPage.types';
import { AskSession } from '@/apis/ask/askHistory/types';

// ============= Component =============

/**
 * DocumentsPage Component
 *
 * Main component for managing documents within a workspace.
 * Handles document upload, collection management, and document viewing.
 *
 * @returns JSX.Element - The rendered documents page
 */
export const DocumentsPage = () => {
  // ============= Hooks =============

  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { getAccessToken } = UseAuth();
  const { isCompactTheme } = useCompactTheme();
  const { handleError, handleWarning, handleInfo } =
    useWorkspaceErrorHandler(id);

  // Filter state for "All Files", "Recent", "Shared with Me"
  const [documentFilter, setDocumentFilter] = useState<string>('all');
  const [hasHandledInitialQueryAction, setHasHandledInitialQueryAction] =
    useState(false);
  const [multiSelectEnabled, setMultiSelectEnabled] = useState(false);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [showDeleteDocumentsModal, setShowDeleteDocumentsModal] =
    useState(false);
  const [deleteProgress, setDeleteProgress] = useState({
    completed: 0,
    total: 0,
  });
  const [pendingDeletionDocuments, setPendingDeletionDocuments] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [latestDeletedDocumentName, setLatestDeletedDocumentName] = useState<
    string | null
  >(null);
  // Seconds elapsed while files are processing server-side, so a long
  // (multi-minute) wait reads as expected rather than broken.
  const [processingElapsed, setProcessingElapsed] = useState(0);

  // Set up SignalR for workspace messages
  // Note: workspaceHub is initialized but connection is managed by useWorkspaceRelayHubHandlers
  useSignalRHub({ hubName: 'workspaceRelayHub' });

  // ============= State (from useDocuments hook) =============

  const {
    // State
    workspaceData,
    isLoading,
    isUploading,
    isProcessing,
    currentOperation,
    uploadProgress,
    uploadError,
    error,
    showUploadModal,
    uploadMode,
    showCreateCollectionModal,
    pendingFiles,
    urlInput,
    versionIfExists,
    selectedCollectionId,
    activeCollectionFilter,
    newCollectionName,
    newCollectionDescription,
    showMoveToCollectionModal,
    selectedDocumentForMove,
    targetCollectionId,
    showDocumentSuggestionsModal,
    selectedDocumentForSuggestions,
    documentSuggestions,
    isLoadingDocumentSuggestions,
    showPdfViewerModal,
    selectedDocument,
    pdfStreamUrl,
    isLoadingPdf,
    showLinkFileToAskModal,
    selectedDocumentForLink,
    selectedAskSessionIdForLink,
    // Computed values
    // workflows,
    regularCollections,
    // smartCollections,
    documentsWithCollection,
    filteredDocuments,
    allCollections,
    // Helper functions
    getFileCollectionId,
    getFileCollectionInfo,
    // Actions
    getWorkspaceData,
    confirmFileUpload,
    handleUrlUpload,
    handleDelete,
    handleDownload,
    handleViewDocument,
    handleFileSelect,
    handleGetDocumentSuggestions,
    handleMoveToCollection,
    handleCreateCollection,
    // handleCreateSmartCollection,
    handleLinkFileToAsk,
    clearProcessingState,
    // Setters
    setShowUploadModal,
    setUploadMode,
    setShowCreateCollectionModal,
    setPendingFiles,
    setUrlInput,
    setVersionIfExists,
    setAllowSmartCategorization,
    setSelectedWorkflowId,
    setSelectedAskSessionId,
    setSelectedCollectionId,
    setActiveCollectionFilter,
    setNewCollectionName,
    setNewCollectionDescription,
    setShowMoveToCollectionModal,
    setSelectedDocumentForMove,
    setTargetCollectionId,
    setCollectionSuggestions,
    setShowDocumentSuggestionsModal,
    setSelectedDocumentForSuggestions,
    setDocumentSuggestions,
    setShowPdfViewerModal,
    setSelectedDocument,
    setPdfStreamUrl,
    setIsUploading,
    setUploadError,
    viewMode,
    setViewMode,
    setShowLinkFileToAskModal,
    setSelectedDocumentForLink,
    setSelectedAskSessionIdForLink,
  } = useDocuments({
    workspaceId: id,
    getAccessToken,
  });

  // Error handling is now done via useWorkspaceErrorHandler hook

  // ============= Effects =============

  /**
   * Listen for file upload notifications via SignalR.
   * Note: SignalR group joining and online status tracking is now handled by WorkspaceRouteGuard.
   * This ensures it works consistently across all workspace routes.
   */
  useWorkspaceRelayHubHandlers({
    workspaceId: id,
    onDataRefresh: undefined,
    enableToasts: true,
    enableLogging: true,
    onEventReceived: (event, data, parsed) => {
      const eventData = (data || {}) as {
        param1?: string;
        userMessage?: {
          fileId?: string;
          fileName?: string;
          name?: string;
        };
      };

      if (
        event === WorkspaceRelayHubEvents.DATAROOMFILE_DELETED_FROM_WORKSPACE
      ) {
        const deletedFileId =
          (parsed?.fileId as string | undefined) ||
          eventData.userMessage?.fileId ||
          eventData.param1;
        const deletedFileName =
          (parsed?.fileName as string | undefined) ||
          eventData.userMessage?.fileName ||
          eventData.userMessage?.name ||
          undefined;

        if (deletedFileId || pendingDeletionDocuments.length > 0) {
          setPendingDeletionDocuments((prev) => {
            const matchedDocument = deletedFileId
              ? prev.find((doc) => doc.id === deletedFileId)
              : prev[0];

            setLatestDeletedDocumentName(
              deletedFileName || matchedDocument?.name || null,
            );

            if (!matchedDocument) {
              return prev;
            }

            return prev.filter((doc) => doc.id !== matchedDocument.id);
          });
          setSelectedDocumentIds((prev) =>
            deletedFileId ? prev.filter((id) => id !== deletedFileId) : prev,
          );
          setDeleteProgress((prev) => ({
            ...prev,
            completed: Math.min(prev.total, prev.completed + 1),
          }));
        }

        void getWorkspaceData({ silent: true });
      }

      if (event === WorkspaceRelayHubEvents.DATAROOMFILE_ADDED_TO_WORKSPACE) {
        void getWorkspaceData({ silent: true });
        clearProcessingState();
      }

      if (
        event === WorkspaceRelayHubEvents.FILE_UPLOAD_ERRORED ||
        event === WorkspaceRelayHubEvents.URL_UPLOAD_ERRORED ||
        event === WorkspaceRelayHubEvents.CENTRAL_UPLOAD_ERRORED ||
        event ===
          WorkspaceRelayHubEvents.DATAROOMFILE_DELETE_FROM_WORKSPACE_ERROR
      ) {
        clearProcessingState();
      }
    },
  });

  // Track elapsed processing time for upload operations to set expectations.
  useEffect(() => {
    const isUploadActive =
      currentOperation === 'upload' && (isUploading || isProcessing);

    if (!isUploadActive) {
      return;
    }

    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      setProcessingElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => {
      window.clearInterval(interval);
      setProcessingElapsed(0);
    };
  }, [currentOperation, isUploading, isProcessing]);

  useEffect(() => {
    if (
      currentOperation === 'delete' &&
      deleteProgress.total > 0 &&
      deleteProgress.completed >= deleteProgress.total
    ) {
      clearProcessingState();
      setDeleteProgress({ completed: 0, total: 0 });
      setPendingDeletionDocuments([]);
      setLatestDeletedDocumentName(null);
      setMultiSelectEnabled(false);
    }
  }, [clearProcessingState, currentOperation, deleteProgress]);

  /**
   * Handle deep-link query params, e.g.:
   * - ?upload=1 to open the upload modal
   * - ?fileId={id} to open a specific document in the PDF viewer
   */
  useEffect(() => {
    if (hasHandledInitialQueryAction) {
      return;
    }

    // Only act once workspace data is available
    if (!workspaceData) {
      return;
    }

    const shouldOpenUpload = searchParams.get('upload') === '1';
    const initialFileId = searchParams.get('fileId');

    if (shouldOpenUpload) {
      setShowUploadModal(true);
      setHasHandledInitialQueryAction(true);
      return;
    }

    if (initialFileId && documentsWithCollection.length > 0) {
      const doc = documentsWithCollection.find(
        (d) => d.dataRoomFileId === initialFileId,
      );
      if (doc) {
        void handleViewDocument(doc);
      }
      setHasHandledInitialQueryAction(true);
    }
  }, [
    documentsWithCollection,
    handleViewDocument,
    hasHandledInitialQueryAction,
    searchParams,
    setShowUploadModal,
    workspaceData,
  ]);

  // Filter documents by documentFilter (all, recent)
  const getFilteredDocumentsByFilter = useMemo(() => {
    let filtered = filteredDocuments;

    if (documentFilter === 'recent') {
      // Show documents added in the last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      filtered = filteredDocuments.filter((doc) => {
        if (!doc.added) return false;
        const addedDate = new Date(doc.added);
        return addedDate >= thirtyDaysAgo;
      });

      // Sort by most recent first
      filtered.sort((a, b) => {
        const dateA = a.added ? new Date(a.added).getTime() : 0;
        const dateB = b.added ? new Date(b.added).getTime() : 0;
        return dateB - dateA;
      });
    }

    return filtered;
  }, [filteredDocuments, documentFilter]);

  // ============= Event Handlers =============

  /**
   * Resets the upload modal state to initial values.
   * Called when the upload modal is closed or cancelled.
   */
  const resetUploadModal = () => {
    setShowUploadModal(false);
    setPendingFiles([]);
    setUrlInput('');
    setCollectionSuggestions({});
    setSelectedWorkflowId('');
    setSelectedAskSessionId('');
    setSelectedCollectionId('');
    setAllowSmartCategorization(true);
  };

  /**
   * Resets the create collection modal state.
   */
  const resetCreateCollectionModal = () => {
    setShowCreateCollectionModal(false);
    setNewCollectionName('');
    setNewCollectionDescription('');
  };

  /**
   * Resets the create smart collection modal state.
   */
  // const resetCreateSmartCollectionModal = () => {
  //   setShowCreateSmartCollectionModal(false);
  //   setNewSmartCollectionName('');
  //   setNewSmartCollectionDescription('');
  // };

  /**
   * Resets the move to collection modal state.
   */
  const resetMoveToCollectionModal = () => {
    setShowMoveToCollectionModal(false);
    setSelectedDocumentForMove(null);
    setTargetCollectionId('');
  };

  /**
   * Resets the document suggestions modal state.
   */
  const resetDocumentSuggestionsModal = () => {
    setShowDocumentSuggestionsModal(false);
    setSelectedDocumentForSuggestions(null);
    setDocumentSuggestions([]);
  };

  /**
   * Resets the PDF viewer modal state.
   */
  const resetPdfViewerModal = () => {
    setShowPdfViewerModal(false);
    setSelectedDocument(null);
    setPdfStreamUrl(null);
  };

  /**
   * Resets the link file to ask modal state.
   */
  const resetLinkFileToAskModal = () => {
    setShowLinkFileToAskModal(false);
    setSelectedDocumentForLink(null);
    setSelectedAskSessionIdForLink('');
  };

  const resetDeleteDocumentsModal = () => {
    setShowDeleteDocumentsModal(false);
  };

  const toggleDocumentSelection = (doc: DataRoomFile) => {
    setSelectedDocumentIds((prev) =>
      prev.includes(doc.dataRoomFileId)
        ? prev.filter((id) => id !== doc.dataRoomFileId)
        : [...prev, doc.dataRoomFileId],
    );
  };

  const handleToggleMultiSelect = () => {
    setMultiSelectEnabled((prev) => !prev);
    setSelectedDocumentIds([]);
  };

  const selectedDocumentsForDeletion = useMemo(
    () =>
      documentsWithCollection.filter((doc) =>
        selectedDocumentIds.includes(doc.dataRoomFileId),
      ),
    [documentsWithCollection, selectedDocumentIds],
  );

  const handleConfirmDeleteDocuments = async () => {
    if (selectedDocumentIds.length === 0) {
      return;
    }

    setPendingDeletionDocuments(
      selectedDocumentsForDeletion.map((doc) => ({
        id: doc.dataRoomFileId,
        name: doc.name || 'Untitled Document',
      })),
    );
    setLatestDeletedDocumentName(null);
    const requestedCount = selectedDocumentIds.length;
    setDeleteProgress({
      completed: 0,
      total: requestedCount,
    });
    setShowDeleteDocumentsModal(false);
    const submittedCount = await handleDelete(selectedDocumentIds);

    if (submittedCount > 0) {
      setDeleteProgress((prev) => ({
        completed: Math.min(prev.completed, submittedCount),
        total: submittedCount,
      }));
      return;
    }

    clearProcessingState();
    setDeleteProgress({ completed: 0, total: 0 });
    setPendingDeletionDocuments([]);
    setLatestDeletedDocumentName(null);
  };

  /**
   * Handles switching upload mode and resetting related state.
   *
   * @param mode - The upload mode to switch to
   */
  const handleUploadModeChange = (mode: UploadMode) => {
    setUploadMode(mode);
    if (mode === 'files') {
      setUrlInput('');
    } else {
      setPendingFiles([]);
      setCollectionSuggestions({});
    }
  };

  /**
   * Handles collection selection in upload modal.
   * Disables smart categorization when a collection is manually selected.
   *
   * @param collectionId - The selected collection ID
   */
  const handleCollectionSelect = (collectionId: string) => {
    setSelectedCollectionId(collectionId);
    // If a collection is selected, disable smart categorization
    if (collectionId) {
      setAllowSmartCategorization(false);
    }
  };

  /**
   * Handles smart categorization checkbox change.
   * Clears collection selection when enabling smart categorization.
   *
   * @param enabled - Whether smart categorization is enabled
   */
  // const handleSmartCategorizationChange = (enabled: boolean) => {
  //   setAllowSmartCategorization(enabled);
  //   // If enabling smart categorization, clear collection selection
  //   if (enabled) {
  //     setSelectedCollectionId('');
  //   }
  // };

  /**
   * Handles creating a new smart collection from a suggestion and uploading files.
   * This is a complex operation that:
   * 1. Creates the collection via API
   * 2. Extracts the collection ID from the response
   * 3. Uploads files with the new collection ID
   * 4. Refreshes workspace data
   *
   * @param suggestion - The collection suggestion to create
   */
  // const handleCreateCollectionAndUpload = async (suggestion: CollectionSuggestion) => {
  //   try {
  //     setIsUploading(true);

  //     // Step 1: Create the collection
  //     const collectionResponse = await postCollection({
  //       getAccessToken,
  //       workspaceId: id || '',
  //       body: {
  //         name: suggestion.collectionName,
  //         description: suggestion.description || '',
  //         // Smart Collections temporarily disabled - not properly set up
  //         isSmartCollection: false,
  //         // isSmartCollection: true,
  //       },
  //     });

  //     // Step 2: Extract collection ID from response
  //     let newCollectionId = extractCollectionIdFromResponse(collectionResponse);

  //     // Step 3: If ID not found in response, refresh and find it
  //     if (!newCollectionId) {
  //       await getWorkspaceData();
  //       const updatedCollections =
  //         (workspaceData?.dataRoom?.workspaceDataRoomCollections?.items as any[]) || [];
  //       const newCollection = findCollectionByName(
  //         updatedCollections,
  //         suggestion.collectionName,
  //         true
  //       );
  //       newCollectionId = extractCollectionId(newCollection || {});
  //     }

  //     if (!newCollectionId) {
  //       throw new Error('Failed to get collection ID after creation');
  //     }

  //     // Step 4: Set the collection ID and disable smart categorization
  //     setSelectedCollectionId(newCollectionId);
  //     setAllowSmartCategorization(false);

  //     // Step 5: Close modal and upload files
  //     setShowUploadModal(false);

  //     const uploadResponse = await uploadLocalDocuments({
  //       getAccessToken,
  //       workspaceId: id || '',
  //       files: pendingFiles,
  //       versionIfExists,
  //       workflowInstanceId: selectedWorkflowId || undefined,
  //       collectionId: newCollectionId,
  //     });

  //     // Step 6: Reset form
  //     setPendingFiles([]);
  //     setSelectedWorkflowId('');
  //     setSelectedCollectionId('');
  //     setCollectionSuggestions({});
  //     setAllowSmartCategorization(true);

  //     // Step 7: Refresh workspace data if upload succeeded
  //     if (uploadResponse && (uploadResponse.success || uploadResponse.data)) {
  //       await getWorkspaceData();
  //     }

  //     handleSuccess(
  //       `Collection "${suggestion.collectionName}" created and files uploaded successfully!`
  //     );
  //   } catch (err) {
  //     // Action error: Log, show toast
  //     const error =
  //       err instanceof Error ? err : new Error('Failed to create collection and upload');

  //     // Extract response data if available (for Azure error parsing)
  //     const response = (err as any)?.response || (err as any)?.body || error.message;

  //     // Log for debugging
  //     console.error('Failed to create collection and upload:', error);

  //     // Log to error boundary and show toast
  //     handleError(
  //       error instanceof ApiError ? error : error,
  //       'Failed to create collection and upload',
  //       response
  //     );
  //   } finally {
  //     setIsUploading(false);
  //   }
  // };

  /**
   * Handles using an existing collection from a suggestion.
   *
   * @param suggestion - The collection suggestion to use
   */
  // const handleUseExistingCollection = (suggestion: CollectionSuggestion) => {
  //   if (suggestion.collectionId) {
  //     setSelectedCollectionId(suggestion.collectionId);
  //     setAllowSmartCategorization(false);
  //   }
  // };

  /**
   * Handles creating a collection from document suggestions and moving the document.
   *
   * @param suggestion - The collection suggestion
   */
  const handleCreateCollectionFromSuggestion = async (
    suggestion: CollectionSuggestion,
  ) => {
    try {
      setIsUploading(true);

      await postCollection({
        getAccessToken,
        workspaceId: id || '',
        body: {
          name: suggestion.collectionName,
          description: suggestion.description || '',
          // Smart Collections temporarily disabled - not properly set up
          isSmartCollection: false,
          // isSmartCollection: true,
        },
      });

      // Refresh to get the new collection ID
      await getWorkspaceData();

      // Wait for refresh to complete, then find and move document
      setTimeout(async () => {
        await getWorkspaceData();
        const updatedCollections =
          (workspaceData?.dataRoom?.workspaceDataRoomCollections
            ?.items as Collection[]) || [];
        const newCollection = findCollectionByName(
          updatedCollections,
          suggestion.collectionName,
          true,
        );

        if (newCollection) {
          const newCollectionId = extractCollectionId(newCollection);
          if (newCollectionId && selectedDocumentForSuggestions) {
            // Move document to new collection
            setSelectedDocumentForMove(selectedDocumentForSuggestions);
            setTargetCollectionId(newCollectionId);
            await handleMoveToCollection();
            setSelectedDocumentForMove(null);
            setTargetCollectionId('');
          }
        }
      }, getCollectionRefreshDelay());

      setShowDocumentSuggestionsModal(false);
      handleInfo(
        `Collection "${suggestion.collectionName}" created successfully! Moving document...`,
      );
    } catch (err) {
      // Action error: Log, show toast
      const error =
        err instanceof Error
          ? err
          : new Error('Failed to create collection from suggestion');

      // Extract response data if available (for Azure error parsing)
      const errorWithResponse = err as { response?: unknown; body?: unknown };
      const response =
        errorWithResponse.response || errorWithResponse.body || error.message;

      // Log for debugging
      console.error('Failed to create collection from suggestion:', error);

      // Log to error boundary and show toast
      handleError(
        error instanceof ApiError ? error : error,
        'Failed to create collection from suggestion',
        response,
      );
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Handles moving document to an existing collection from suggestions.
   *
   * @param suggestion - The collection suggestion
   */
  const handleMoveToExistingCollection = async (
    suggestion: CollectionSuggestion,
  ) => {
    if (!suggestion.collectionId || !selectedDocumentForSuggestions) {
      return;
    }

    const currentCollectionId = getFileCollectionId
      ? getFileCollectionId(selectedDocumentForSuggestions.dataRoomFileId)
      : null;

    if (suggestion.collectionId === currentCollectionId) {
      handleWarning('Document is already in this collection');
      return;
    }

    setSelectedDocumentForMove(selectedDocumentForSuggestions);
    setTargetCollectionId(suggestion.collectionId);
    setShowDocumentSuggestionsModal(false);
    await handleMoveToCollection();
    setSelectedDocumentForMove(null);
    setTargetCollectionId('');
  };

  /**
   * Gets the current collection ID for a document.
   * Handles various ID field names that may exist.
   *
   * @param document - The document to get collection ID for
   * @returns Collection ID or empty string
   */
  const getCurrentCollectionId = (document: DataRoomFile | null): string => {
    if (!document) return '';

    const id = extractDocumentCollectionId(document, getFileCollectionId);
    return id || '';
  };

  // ============= Upload Helpers =============

  /** Maximum allowed size per file (100 MB). */
  const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;

  /**
   * Formats a byte count into a human-readable size string.
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /**
   * Formats elapsed seconds into a compact mm:ss / Ns string.
   */
  const formatElapsed = (totalSeconds: number): string => {
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  };

  /** Files that exceed the size limit, surfaced as a pre-upload warning. */
  const oversizedFiles = useMemo(
    () => pendingFiles.filter((file) => file.size > MAX_FILE_SIZE_BYTES),
    [pendingFiles, MAX_FILE_SIZE_BYTES],
  );

  const hasOversizedFiles = oversizedFiles.length > 0;

  /**
   * Removes a single file from the pending selection before upload.
   */
  const handleRemovePendingFile = (index: number) => {
    setPendingFiles(pendingFiles.filter((_, i) => i !== index));
  };

  /**
   * Re-attempts the last failed upload using the still-selected files/URLs.
   */
  const handleRetryUpload = () => {
    if (!uploadError) {
      return;
    }
    setUploadError(null);
    if (uploadError.mode === 'url') {
      void handleUrlUpload();
    } else {
      void confirmFileUpload();
    }
  };

  // ============= Render Helpers =============

  /**
   * Renders the upload mode toggle buttons.
   */
  const renderUploadModeToggle = () => (
    <ToolbarContainer>
      <Button
        variant={uploadMode === 'files' ? 'primary' : 'outline'}
        onClick={() => handleUploadModeChange('files')}
        style={{ flex: 1 }}
        leftIcon={<FiFile />}
      >
        Upload Files
      </Button>
      <Button
        variant={uploadMode === 'url' ? 'primary' : 'outline'}
        onClick={() => handleUploadModeChange('url')}
        style={{ flex: 1 }}
        leftIcon={<FiLink />}
      >
        Import from URL
      </Button>
    </ToolbarContainer>
  );

  /**
   * Renders the file upload section of the upload modal.
   */
  const renderFileUploadSection = () => (
    <FormGroup>
      <Input
        type="file"
        label="Select Files"
        multiple
        onFileSelect={handleFileSelect}
        tooltip="Supports PDF, DOCX, TXT, and more • Auto-OCR • Smart categorization"
      />
      {pendingFiles.length > 0 && (
        <>
          <ModalSectionLabel style={{ marginTop: '0.75rem' }}>
            {pendingFiles.length} file{pendingFiles.length === 1 ? '' : 's'}{' '}
            selected
          </ModalSectionLabel>
          <PendingFileList>
            {pendingFiles.map((file, index) => {
              const isOversized = file.size > MAX_FILE_SIZE_BYTES;
              return (
                <PendingFileRow key={`${file.name}-${index}`}>
                  <PendingFileInfo>
                    <span>{file.name}</span>
                    <span>
                      {formatFileSize(file.size)}
                      {isOversized ? ' • exceeds 100 MB limit' : ''}
                    </span>
                  </PendingFileInfo>
                  <PendingFileRemove
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    disabled={isUploading}
                    onClick={() => handleRemovePendingFile(index)}
                  >
                    <FiX />
                  </PendingFileRemove>
                </PendingFileRow>
              );
            })}
          </PendingFileList>
          {hasOversizedFiles && (
            <PendingFileError>
              {oversizedFiles.length} file
              {oversizedFiles.length === 1 ? '' : 's'} exceed the 100 MB limit.
              Remove {oversizedFiles.length === 1 ? 'it' : 'them'} to continue.
            </PendingFileError>
          )}
        </>
      )}
    </FormGroup>
  );

  /**
   * Renders the URL upload section of the upload modal.
   */
  const renderUrlUploadSection = () => (
    <FormGroup>
      <FormLabel>URLs (one per line)</FormLabel>
      <FormTextarea
        value={urlInput}
        onChange={(e) => setUrlInput(e.target.value)}
        placeholder="https://example.com/document.pdf&#10;https://example.com/file.docx"
        rows={6}
      />
    </FormGroup>
  );

  /**
   * Renders AI collection suggestions for pending files.
   * Temporarily disabled - not properly set up
   */
  // const renderCollectionSuggestions = () => {
  //   if (!allowSmartCategorization || uploadMode !== 'files' || pendingFiles.length === 0) {
  //     return null;
  //   }

  //   return (
  //     <FormGroup>
  //       <FormLabel>
  //         🤖 AI Collection Suggestions
  //         {isLoadingSuggestions && (
  //           <span
  //             style={{
  //               fontSize: '0.75rem',
  //               color: '#666',
  //               marginLeft: '0.5rem',
  //               fontWeight: 'normal',
  //             }}
  //           >
  //             (Analyzing files...)
  //           </span>
  //         )}
  //       </FormLabel>
  //       <div
  //         style={{
  //           display: 'flex',
  //           flexDirection: 'column',
  //           gap: '1rem',
  //           marginTop: '0.5rem',
  //         }}
  //       >
  //         {pendingFiles.map((file) => {
  //           const suggestions = collectionSuggestions[file.name] || [];
  //           if (suggestions.length === 0 && !isLoadingSuggestions) {
  //             return null;
  //           }

  //           return (
  //             <div
  //               key={file.name}
  //               style={{
  //                 border: '1px solid #e0e0e0',
  //                 borderRadius: '8px',
  //                 padding: '0.75rem',
  //                 backgroundColor: '#f9f9f9',
  //               }}
  //             >
  //               <div
  //                 style={{
  //                   fontSize: '0.875rem',
  //                   fontWeight: 600,
  //                   marginBottom: '0.5rem',
  //                   color: '#333',
  //                 }}
  //               >
  //                 {file.name}
  //               </div>
  //               {isLoadingSuggestions ? (
  //                 <div
  //                   style={{
  //                     fontSize: '0.75rem',
  //                     color: '#666',
  //                     fontStyle: 'italic',
  //                   }}
  //                 >
  //                   Analysing document...
  //                 </div>
  //               ) : suggestions.length > 0 ? (
  //                 <div
  //                   style={{
  //                     display: 'flex',
  //                     flexDirection: 'column',
  //                     gap: '0.5rem',
  //                   }}
  //                 >
  //                   {suggestions.map((suggestion, idx) => (
  //                     <div
  //                       key={idx}
  //                       style={{
  //                         display: 'flex',
  //                         alignItems: 'center',
  //                         justifyContent: 'space-between',
  //                         padding: '0.5rem',
  //                         backgroundColor: 'white',
  //                         borderRadius: '4px',
  //                         border: '1px solid #e0e0e0',
  //                       }}
  //                     >
  //                       <div style={{ flex: 1 }}>
  //                         <div
  //                           style={{
  //                             display: 'flex',
  //                             alignItems: 'center',
  //                             gap: '0.5rem',
  //                           }}
  //                         >
  //                           <span
  //                             style={{
  //                               fontSize: '0.875rem',
  //                               fontWeight: 500,
  //                             }}
  //                           >
  //                             {getCollectionIcon(!!suggestion.collectionId)}{' '}
  //                             {suggestion.collectionName}
  //                           </span>
  //                           <span
  //                             style={{
  //                               fontSize: '0.7rem',
  //                               color: '#666',
  //                               backgroundColor: '#f0f0f0',
  //                               padding: '0.125rem 0.375rem',
  //                               borderRadius: '4px',
  //                             }}
  //                           >
  //                             {formatConfidence(suggestion.confidence)} match
  //                           </span>
  //                         </div>
  //                         {suggestion.reason && (
  //                           <div
  //                             style={{
  //                               fontSize: '0.7rem',
  //                               color: '#999',
  //                               marginTop: '0.25rem',
  //                             }}
  //                           >
  //                             {suggestion.reason}
  //                           </div>
  //                         )}
  //                       </div>
  //                       <Button
  //                         variant="outline"
  //                         onClick={async () => {
  //                           if (suggestion.collectionId) {
  //                             handleUseExistingCollection(suggestion);
  //                           } else {
  //                             await handleCreateCollectionAndUpload(suggestion);
  //                           }
  //                         }}
  //                         disabled={isUploading}
  //                         style={{
  //                           fontSize: '0.75rem',
  //                           padding: '0.25rem 0.75rem',
  //                         }}
  //                       >
  //                         {suggestion.collectionId ? 'Use' : 'Create & Use'}
  //                       </Button>
  //                     </div>
  //                   ))}
  //                 </div>
  //               ) : (
  //                 <div
  //                   style={{
  //                     fontSize: '0.75rem',
  //                     color: '#999',
  //                     fontStyle: 'italic',
  //                   }}
  //                 >
  //                   No suggestions available
  //                 </div>
  //               )}
  //             </div>
  //           );
  //         })}
  //       </div>
  //     </FormGroup>
  //   );
  // };

  // ============= Early Returns =============

  if (isLoading) {
    return <LoadingSpinner variant="fullPage" />;
  }

  if (error && !workspaceData) {
    return (
      <PlaceholderText>
        Error loading workspace: {error.message}
      </PlaceholderText>
    );
  }

  if (!workspaceData) {
    return <PlaceholderText>No workspace data found</PlaceholderText>;
  }

  // ============= Render =============

  const nextDeletionDocumentName = pendingDeletionDocuments[0]?.name || null;
  const deleteDisplayStep =
    deleteProgress.total > 0
      ? Math.min(deleteProgress.completed + 1, deleteProgress.total)
      : 0;

  return (
    <PageContainer>
      {!isLoading && workspaceData && (
        <WorkspaceHeaderWithErrorBoundary
          workspaceData={workspaceData}
          activePage="documents"
        >
          {/* Display workspace errors visually */}
          <WorkspaceErrorDisplay workspaceId={workspaceData.id} />
          <ContentSectionContainer $isCompact={isCompactTheme}>
            <ContentSection $isCompact={isCompactTheme}>
              {/* Toolbar */}
              <DocumentsToolbar
                activeCollectionFilter={activeCollectionFilter}
                setActiveCollectionFilter={setActiveCollectionFilter}
                documentsWithCollection={documentsWithCollection}
                // Smart Collections temporarily disabled - not properly set up
                // setShowCreateSmartCollectionModal={setShowCreateSmartCollectionModal}
                setShowCreateSmartCollectionModal={() => {}}
                setShowCreateCollectionModal={setShowCreateCollectionModal}
                setShowUploadModal={setShowUploadModal}
                setUploadMode={setUploadMode}
                viewMode={viewMode}
                setViewMode={setViewMode}
                multiSelectEnabled={multiSelectEnabled}
                selectedCount={selectedDocumentIds.length}
                onToggleMultiSelect={handleToggleMultiSelect}
                onDeleteSelected={() => setShowDeleteDocumentsModal(true)}
                isCompact={isCompactTheme}
              />

              {/* Upload Status Tile - Shows when files are being uploaded/processed or deleted */}
              {(isUploading || isProcessing) && (
                <UploadStatusTile $isCompact={isCompactTheme}>
                  <UploadStatusIcon $isCompact={isCompactTheme}>
                    {currentOperation === 'delete' ? (
                      <FiTrash2 />
                    ) : (
                      <FiUploadCloud />
                    )}
                  </UploadStatusIcon>
                  <UploadStatusContent>
                    <UploadStatusTitle $isCompact={isCompactTheme}>
                      {currentOperation === 'delete'
                        ? `Deleting ${deleteDisplayStep} of ${Math.max(
                            deleteProgress.total,
                            1,
                          )} document${
                            Math.max(deleteProgress.total, 1) === 1 ? '' : 's'
                          }...`
                        : isUploading
                          ? `Uploading${
                              uploadProgress !== null
                                ? ` ${uploadProgress}%`
                                : '…'
                            }`
                          : 'Processing your files'}
                    </UploadStatusTitle>
                    <UploadStatusMessage $isCompact={isCompactTheme}>
                      {currentOperation === 'delete'
                        ? nextDeletionDocumentName
                          ? `Currently processing: ${nextDeletionDocumentName}${
                              latestDeletedDocumentName
                                ? `. Latest confirmation: ${latestDeletedDocumentName}.`
                                : '.'
                            }`
                          : latestDeletedDocumentName
                            ? `Latest confirmation: ${latestDeletedDocumentName}. Waiting for any remaining delete confirmations.`
                            : `Deleting ${deleteProgress.total} marked ${
                                deleteProgress.total === 1
                                  ? 'document'
                                  : 'documents'
                              }. Progress updates as we get confirmation of each deletion.`
                        : isUploading
                          ? 'Sending your files to the workspace. Please keep this tab open.'
                          : `This can take a few minutes for large or scanned documents — you'll be notified when processing finishes. Elapsed: ${formatElapsed(
                              processingElapsed,
                            )}.`}
                    </UploadStatusMessage>
                    {currentOperation === 'upload' &&
                      isUploading &&
                      uploadProgress !== null && (
                        <UploadProgressBarTrack>
                          <UploadProgressBarFill $value={uploadProgress} />
                        </UploadProgressBarTrack>
                      )}
                  </UploadStatusContent>
                </UploadStatusTile>
              )}

              {/* Upload Error Tile - offers a retry after a failed upload */}
              {uploadError && !isUploading && !isProcessing && (
                <UploadStatusTile $isCompact={isCompactTheme}>
                  <UploadStatusIcon $isCompact={isCompactTheme}>
                    <FiAlertTriangle />
                  </UploadStatusIcon>
                  <UploadStatusContent>
                    <UploadStatusTitle $isCompact={isCompactTheme}>
                      Upload failed
                    </UploadStatusTitle>
                    <UploadStatusMessage $isCompact={isCompactTheme}>
                      {uploadError.message ||
                        'Something went wrong while uploading. You can try again.'}
                    </UploadStatusMessage>
                  </UploadStatusContent>
                  <UploadStatusActions>
                    <Button
                      variant="outline"
                      size={isCompactTheme ? 'sm' : 'md'}
                      leftIcon={<FiRefreshCw />}
                      onClick={handleRetryUpload}
                    >
                      Retry
                    </Button>
                    <Button
                      variant="ghost"
                      size={isCompactTheme ? 'sm' : 'md'}
                      onClick={() => setUploadError(null)}
                    >
                      Dismiss
                    </Button>
                  </UploadStatusActions>
                </UploadStatusTile>
              )}

              {/* Filter Pills - Only show in file view */}
              {viewMode === 'file' && (
                <FilterPills
                  label="Filters"
                  options={[
                    {
                      id: 'all',
                      label: `All Files (${documentsWithCollection.length})`,
                    },
                    { id: 'recent', label: 'Recent' },
                  ]}
                  currentFilter={documentFilter}
                  onFilterChange={setDocumentFilter}
                />
              )}

              {/* Main Content Area */}
              {viewMode === 'file' ? (
                <>
                  {/* Collections Section */}
                  <CollectionsSection
                    // Smart Collections temporarily disabled - not properly set up
                    smartCollections={[]}
                    // smartCollections={smartCollections}
                    regularCollections={regularCollections}
                    activeCollectionFilter={activeCollectionFilter}
                    onCollectionClick={setActiveCollectionFilter}
                    isCompact={isCompactTheme}
                  />

                  {/* Documents Grid or Empty State */}
                  {isLoading && getFilteredDocumentsByFilter.length === 0 ? (
                    <LoadingSpinner variant="fullPage" />
                  ) : getFilteredDocumentsByFilter.length === 0 ? (
                    <EmptyState
                      icon="📁"
                      title={
                        documentFilter === 'recent'
                          ? 'No recent documents'
                          : activeCollectionFilter
                            ? 'No documents in this collection'
                            : 'No documents yet'
                      }
                      subtitle={
                        documentFilter === 'recent'
                          ? 'No documents were added in the last 30 days'
                          : activeCollectionFilter
                            ? 'Try selecting a different collection or upload new documents'
                            : 'Upload your first document to get started'
                      }
                      isCompact={isCompactTheme}
                    />
                  ) : (
                    <DocumentGrid
                      documents={getFilteredDocumentsByFilter}
                      onDocumentClick={(doc) => handleViewDocument(doc)}
                      onDownload={handleDownload}
                      onGetSuggestions={handleGetDocumentSuggestions}
                      onMoveToCollection={(doc) => {
                        setSelectedDocumentForMove(doc);
                        setShowMoveToCollectionModal(true);
                      }}
                      onDelete={(doc) => {
                        setSelectedDocumentIds([doc.dataRoomFileId]);
                        setShowDeleteDocumentsModal(true);
                      }}
                      selectedDocumentIds={selectedDocumentIds}
                      onToggleSelect={toggleDocumentSelection}
                      multiSelectEnabled={multiSelectEnabled}
                      getFileCollectionInfo={getFileCollectionInfo}
                      onView={(doc) => handleViewDocument(doc)}
                      onLinkToAsk={(doc) => {
                        setSelectedDocumentForLink(doc);
                        setShowLinkFileToAskModal(true);
                      }}
                      workspaceId={id}
                      isCompact={isCompactTheme}
                    />
                  )}
                </>
              ) : (
                <FolderView
                  // Smart Collections temporarily disabled - not properly set up
                  smartCollections={[]}
                  // smartCollections={smartCollections}
                  regularCollections={regularCollections}
                  allCollections={allCollections}
                  documentsWithCollection={getFilteredDocumentsByFilter}
                  activeCollectionFilter={activeCollectionFilter}
                  onCollectionClick={setActiveCollectionFilter}
                  onDocumentClick={(doc) => handleViewDocument(doc)}
                  onDownload={handleDownload}
                  onGetSuggestions={handleGetDocumentSuggestions}
                  onMoveToCollection={(doc) => {
                    setSelectedDocumentForMove(doc);
                    setShowMoveToCollectionModal(true);
                  }}
                  onDelete={(docId) => handleDelete(docId)}
                  getFileCollectionInfo={getFileCollectionInfo}
                  onView={(doc) => handleViewDocument(doc)}
                  onLinkToAsk={(doc: DataRoomFile) => {
                    setSelectedDocumentForLink(doc);
                    setShowLinkFileToAskModal(true);
                  }}
                  workspaceId={id}
                  isCompact={isCompactTheme}
                />
              )}

              {/* Upload Documents Modal */}
              <Modal
                isOpen={showUploadModal}
                onClose={resetUploadModal}
                title="Upload Documents"
                size="large"
                footer={
                  <FormActions>
                    <Button variant="outline" onClick={resetUploadModal}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => {
                        if (uploadMode === 'files') {
                          confirmFileUpload();
                        } else {
                          handleUrlUpload();
                        }
                      }}
                      disabled={
                        isUploading ||
                        (uploadMode === 'files' &&
                          (pendingFiles.length === 0 || hasOversizedFiles)) ||
                        (uploadMode === 'url' && !urlInput.trim())
                      }
                    >
                      {isUploading
                        ? 'Uploading...'
                        : uploadMode === 'files'
                          ? 'Upload Files'
                          : 'Upload'}
                    </Button>
                  </FormActions>
                }
              >
                {renderUploadModeToggle()}

                {uploadMode === 'files'
                  ? renderFileUploadSection()
                  : renderUrlUploadSection()}

                {/* Collection Selection */}
                <FormGroup>
                  <FormLabel>Add to Collection (Optional)</FormLabel>
                  <FormSelect
                    value={selectedCollectionId}
                    onChange={(e) => handleCollectionSelect(e.target.value)}
                  >
                    <option value="">None - Add to workspace</option>
                    {allCollections.map((collection: Collection) => (
                      <option
                        key={collection.id || collection.collectionId}
                        value={collection.id || collection.collectionId}
                      >
                        {getCollectionDisplayName(collection)}
                      </option>
                    ))}
                  </FormSelect>
                </FormGroup>

                {/* AI Collection Suggestions temporarily disabled - not properly set up */}
                {/* {renderCollectionSuggestions()} */}

                {/* Workflow Selection */}
                {/* TODO: Removed as we haven't  yet implemented workflows */}
                {/* <FormGroup>
                  <FormLabel>Add to Workflow (Optional)</FormLabel>
                  <FormSelect
                    value={selectedWorkflowId}
                    onChange={(e) => setSelectedWorkflowId(e.target.value)}
                  >
                    <option value="">None</option>
                    {workflows.map((workflow: any) => (
                      <option
                        key={workflow.id || workflow.workflowId}
                        value={workflow.id || workflow.workflowId}
                      >
                        {workflow.name || workflow.title || 'Unnamed Workflow'}
                      </option>
                    ))}
                  </FormSelect>
                </FormGroup> */}

                {/* Ask Session Selection */}
                {/* TODO: Hidden as we don't need, but hiding instead of removing as we have no confirmation of deletion */}
                {/* {workspaceData?.askSessions?.length > 0 && (
                  <FormGroup>
                    <FormLabel>Add to Ask Session (Optional)</FormLabel>
                    <FormSelect
                      value={selectedAskSessionId}
                      onChange={(e) => setSelectedAskSessionId(e.target.value)}
                    >
                      <option value="">None</option>
                      {workspaceData?.askSessions?.map((askSession: AskSession) => (
                        <option
                          key={askSession.askSessionId}
                          value={askSession.askSessionId}
                        >
                          {askSession.title || 'Unnamed Ask Session'}
                        </option>
                      ))}
                    </FormSelect>
                  </FormGroup>
                )} */}

                {/* Smart Categorization Toggle - temporarily disabled - not properly set up */}
                {/* <FormGroup>
                  <CheckboxLabel>
                    <CheckboxInput
                      type="checkbox"
                      checked={allowSmartCategorization}
                      onChange={(e) => handleSmartCategorizationChange(e.target.checked)}
                      disabled={!!selectedCollectionId}
                    />
                    Allow smart categorization
                    <span
                      style={{
                        fontSize: '0.75rem',
                        color: '#666',
                        marginLeft: '0.5rem',
                      }}
                    >
                      (AI will automatically organize files into collections)
                    </span>
                  </CheckboxLabel>
                  {selectedCollectionId && (
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#999',
                        marginTop: '0.25rem',
                      }}
                    >
                      Smart categorization is disabled when a collection is selected
                    </div>
                  )}
                </FormGroup> */}

                {/* Version Files Toggle */}
                <FormGroup>
                  <CheckboxLabel>
                    <CheckboxInput
                      type="checkbox"
                      checked={versionIfExists}
                      onChange={(e) => setVersionIfExists(e.target.checked)}
                    />
                    Version files if they already exist
                  </CheckboxLabel>
                </FormGroup>
              </Modal>

              {/* Create Collection Modal */}
              <Modal
                isOpen={showCreateCollectionModal}
                onClose={resetCreateCollectionModal}
                title="Create Collection"
                footer={
                  <FormActions>
                    <Button
                      variant="outline"
                      onClick={resetCreateCollectionModal}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleCreateCollection}
                      disabled={
                        !newCollectionName.trim() ||
                        !newCollectionDescription.trim() ||
                        isUploading
                      }
                    >
                      {isUploading ? 'Creating...' : 'Create Collection'}
                    </Button>
                  </FormActions>
                }
              >
                <FormGroup>
                  <FormLabel>Collection Name *</FormLabel>
                  <FormInput
                    type="text"
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                    placeholder="Enter collection name"
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Description *</FormLabel>
                  <FormTextarea
                    value={newCollectionDescription}
                    onChange={(e) =>
                      setNewCollectionDescription(e.target.value)
                    }
                    placeholder="Enter collection description"
                    rows={4}
                  />
                </FormGroup>
              </Modal>

              {/* Create Smart Collection Modal - temporarily disabled - not properly set up */}
              {/* <Modal
                isOpen={showCreateSmartCollectionModal}
                onClose={resetCreateSmartCollectionModal}
                title="Create Smart Collection"
              >
                <FormGroup>
                  <FormLabel>Smart Collection Name *</FormLabel>
                  <FormInput
                    type="text"
                    value={newSmartCollectionName}
                    onChange={(e) => setNewSmartCollectionName(e.target.value)}
                    placeholder="Enter smart collection name"
                  />
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: '#666',
                      marginTop: '0.5rem',
                    }}
                  >
                    AI will automatically organize files into this collection based on content and
                    patterns
                  </div>
                </FormGroup>
                <FormGroup>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormTextarea
                    value={newSmartCollectionDescription}
                    onChange={(e) => setNewSmartCollectionDescription(e.target.value)}
                    placeholder="Enter collection description"
                    rows={4}
                  />
                </FormGroup>
                <FormActions>
                  <Button variant="outline" onClick={resetCreateSmartCollectionModal}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleCreateSmartCollection}
                    disabled={!newSmartCollectionName.trim() || isUploading}
                  >
                    {isUploading ? 'Creating...' : 'Create Smart Collection'}
                  </Button>
                </FormActions>
              </Modal> */}

              {/* Move to Collection Modal */}
              <Modal
                isOpen={showMoveToCollectionModal && !!selectedDocumentForMove}
                onClose={resetMoveToCollectionModal}
                title="Move Document to Collection"
              >
                {selectedDocumentForMove && (
                  <>
                    <FormGroup>
                      <FormLabel>Document</FormLabel>
                      <ModalDocumentName>
                        {selectedDocumentForMove.name || 'Untitled Document'}
                      </ModalDocumentName>
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>Select Collection</FormLabel>
                      <FormSelect
                        value={
                          targetCollectionId ||
                          getCurrentCollectionId(selectedDocumentForMove)
                        }
                        onChange={(e) => setTargetCollectionId(e.target.value)}
                      >
                        <option value="">None - Remove from collection</option>
                        {allCollections.map((collection: Collection) => {
                          const collectionId = extractCollectionId(collection);
                          const currentCollectionId = getCurrentCollectionId(
                            selectedDocumentForMove,
                          );
                          const isCurrentCollection =
                            collectionId === currentCollectionId;

                          return (
                            <option key={collectionId} value={collectionId}>
                              {getCollectionIcon(collection.isSmartCollection)}{' '}
                              {getCollectionDisplayName(collection)}
                              {isCurrentCollection ? ' (Current)' : ''}
                            </option>
                          );
                        })}
                      </FormSelect>
                    </FormGroup>
                    <FormActions>
                      <Button
                        variant="outline"
                        onClick={resetMoveToCollectionModal}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleMoveToCollection}
                        disabled={isUploading}
                      >
                        {isUploading ? 'Moving...' : 'Move Document'}
                      </Button>
                    </FormActions>
                  </>
                )}
              </Modal>

              {/* Document Suggestions Modal */}
              <Modal
                isOpen={
                  showDocumentSuggestionsModal &&
                  !!selectedDocumentForSuggestions
                }
                onClose={resetDocumentSuggestionsModal}
                title="AI Collection Suggestions"
                size="large"
              >
                {selectedDocumentForSuggestions && (
                  <>
                    <FormGroup>
                      <FormLabel>Document</FormLabel>
                      <ModalDocumentName>
                        {selectedDocumentForSuggestions.name ||
                          'Untitled Document'}
                      </ModalDocumentName>
                      {selectedDocumentForSuggestions.synopsis && (
                        <ModalHintText style={{ marginTop: '0.5rem' }}>
                          {truncateText(
                            selectedDocumentForSuggestions.synopsis,
                          )}
                        </ModalHintText>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>
                        Suggested Collections
                        {isLoadingDocumentSuggestions && (
                          <span
                            style={{
                              fontSize: '0.75rem',
                              color: '#666',
                              marginLeft: '0.5rem',
                              fontWeight: 'normal',
                            }}
                          >
                            (Analyzing document...)
                          </span>
                        )}
                      </FormLabel>
                      {isLoadingDocumentSuggestions ? (
                        <SuggestionEmpty>
                          <LoadingSpinner />
                          <div style={{ marginTop: '1rem' }}>
                            Analyzing document content...
                          </div>
                        </SuggestionEmpty>
                      ) : documentSuggestions.length > 0 ? (
                        <SuggestionList>
                          {documentSuggestions.map((suggestion, idx) => {
                            const currentCollectionId = getFileCollectionId
                              ? getFileCollectionId(
                                  selectedDocumentForSuggestions.dataRoomFileId,
                                )
                              : null;
                            const isCurrentCollection =
                              suggestion.collectionId === currentCollectionId;

                            return (
                              <SuggestionRow
                                key={idx}
                                $current={isCurrentCollection}
                              >
                                <SuggestionInfo>
                                  <SuggestionTitleRow>
                                    <SuggestionName>
                                      {getCollectionIcon(
                                        !!suggestion.collectionId,
                                      )}{' '}
                                      {suggestion.collectionName}
                                    </SuggestionName>
                                    <SuggestionBadge>
                                      {formatConfidence(suggestion.confidence)}{' '}
                                      match
                                    </SuggestionBadge>
                                    {isCurrentCollection && (
                                      <SuggestionCurrentTag>
                                        (Current)
                                      </SuggestionCurrentTag>
                                    )}
                                  </SuggestionTitleRow>
                                  {suggestion.reason && (
                                    <SuggestionReason>
                                      {suggestion.reason}
                                    </SuggestionReason>
                                  )}
                                </SuggestionInfo>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={async () => {
                                    if (suggestion.collectionId) {
                                      await handleMoveToExistingCollection(
                                        suggestion,
                                      );
                                    } else {
                                      await handleCreateCollectionFromSuggestion(
                                        suggestion,
                                      );
                                    }
                                  }}
                                  disabled={
                                    isUploading ||
                                    isCurrentCollection ||
                                    suggestion.collectionId ===
                                      currentCollectionId
                                  }
                                >
                                  {isCurrentCollection
                                    ? 'Current'
                                    : suggestion.collectionId
                                      ? 'Move Here'
                                      : 'Create & Move'}
                                </Button>
                              </SuggestionRow>
                            );
                          })}
                        </SuggestionList>
                      ) : (
                        <SuggestionEmpty>
                          No suggestions available for this document
                        </SuggestionEmpty>
                      )}
                    </FormGroup>
                    <FormActions>
                      <Button
                        variant="outline"
                        onClick={resetDocumentSuggestionsModal}
                      >
                        Close
                      </Button>
                    </FormActions>
                  </>
                )}
              </Modal>

              {/* PDF Viewer Modal */}
              <Modal
                isOpen={showPdfViewerModal && !!selectedDocument}
                onClose={resetPdfViewerModal}
                title={selectedDocument?.name || 'PDF Viewer'}
                size="large"
              >
                {isLoadingPdf ? (
                  <LoadingSpinner variant="fullPage" />
                ) : pdfStreamUrl ? (
                  <PdfViewer data={pdfStreamUrl} />
                ) : (
                  <SuggestionEmpty>Failed to load document</SuggestionEmpty>
                )}
              </Modal>

              {/* Link File to Ask Session Modal */}
              <Modal
                isOpen={showLinkFileToAskModal && !!selectedDocumentForLink}
                onClose={resetLinkFileToAskModal}
                title="Link File to Ask Session"
                footer={
                  <FormActions>
                    <Button variant="outline" onClick={resetLinkFileToAskModal}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleLinkFileToAsk}
                      disabled={!selectedAskSessionIdForLink || isUploading}
                    >
                      {isUploading ? 'Linking...' : 'Link File'}
                    </Button>
                  </FormActions>
                }
              >
                {selectedDocumentForLink && (
                  <>
                    <FormGroup>
                      <FormLabel>Document</FormLabel>
                      <ModalDocumentName>
                        {selectedDocumentForLink.name || 'Untitled Document'}
                      </ModalDocumentName>
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>Select Ask Session *</FormLabel>
                      <FormSelect
                        value={selectedAskSessionIdForLink}
                        onChange={(e) =>
                          setSelectedAskSessionIdForLink(e.target.value)
                        }
                      >
                        <option value="">Select an ask session</option>
                        {workspaceData?.askSessions?.map(
                          (askSession: AskSession) => (
                            <option
                              key={askSession.askSessionId}
                              value={askSession.askSessionId}
                            >
                              {askSession.title || 'Unnamed Ask Session'}
                            </option>
                          ),
                        )}
                      </FormSelect>
                    </FormGroup>
                  </>
                )}
              </Modal>

              <Modal
                isOpen={showDeleteDocumentsModal}
                onClose={resetDeleteDocumentsModal}
                title={
                  selectedDocumentsForDeletion.length > 1
                    ? 'Delete Documents'
                    : 'Delete Document'
                }
                footer={
                  <FormActions>
                    <Button
                      variant="outline"
                      onClick={resetDeleteDocumentsModal}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleConfirmDeleteDocuments}
                      disabled={selectedDocumentsForDeletion.length === 0}
                    >
                      Delete
                    </Button>
                  </FormActions>
                }
              >
                <FormGroup>
                  <FormLabel>
                    {selectedDocumentsForDeletion.length > 1
                      ? 'These documents are marked for deletion'
                      : 'This document is marked for deletion'}
                  </FormLabel>
                  <ModalHintText>
                    Are you sure you want to continue? This will send one delete
                    request per document.
                  </ModalHintText>
                </FormGroup>
                <FormGroup>
                  <DeleteList>
                    {selectedDocumentsForDeletion.map((doc) => (
                      <DeleteListRow key={doc.dataRoomFileId}>
                        {doc.name || 'Untitled Document'}
                      </DeleteListRow>
                    ))}
                  </DeleteList>
                </FormGroup>
              </Modal>
            </ContentSection>
          </ContentSectionContainer>
        </WorkspaceHeaderWithErrorBoundary>
      )}
    </PageContainer>
  );
};
